import { useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, Stack, Dialog, DialogContent, DialogActions, Alert, Skeleton, TextField, FormControlLabel, Switch, Chip,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Campaign as CampaignIcon } from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermission } from '@/hooks/usePermission';
import { useAuthStore } from '@/stores/authStore';
import RichTextEditor from '@/components/common/RichTextEditor';
import apiClient from '@/api/client';
import { notificationsApi, BroadcastPayload } from '@/api/endpoints/notifications';
import { formatDate } from '@/utils/format';
import PageHelpIcon from '@/components/PageHelpIcon';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';

interface Announcement {
  id: string;
  group_id: string;
  title: string;
  body: string;
  is_active: boolean;
  is_pinned: boolean;
  priority?: string;
  posted_by_user_id: string;
  created_at: string;
  updated_at: string;
}

interface AnnouncementFormData {
  title: string;
  body: string;
  is_pinned: boolean;
  priority?: string;
}

export default function AnnouncementsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();
  const { hasPermission } = usePermission();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<AnnouncementFormData>({
    title: '',
    body: '',
    is_pinned: false,
    priority: 'medium',
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState<string | null>(null);

  const isGroupLevel = useAuthStore((s) => s.getActiveCompany()?.is_group_level ?? false);

  const canWrite = hasPermission('portal.write');

  // Broadcast state
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastData, setBroadcastData] = useState<BroadcastPayload>({
    title: '',
    body: '',
    send_email: false,
    company_only: true,
  });

  const broadcastMutation = useMutation({
    mutationFn: (data: BroadcastPayload) =>
      notificationsApi.broadcast(data).then((res) => res.data),
    onSuccess: (result) => {
      const parts = [`${result.in_app_sent} in-app notification(s) sent`];
      if (result.email_sent > 0) parts.push(`${result.email_sent} email(s) sent`);
      if (result.email_failed > 0) parts.push(`${result.email_failed} email(s) failed`);
      showSuccess(parts.join(', '));
      setBroadcastOpen(false);
      setBroadcastData({ title: '', body: '', send_email: false, company_only: true });
    },
    onError: (err: any) => {
      showError(err.response?.data?.detail || err.message || 'Failed to send broadcast');
    },
  });

  const handleBroadcast = () => {
    if (!broadcastData.title.trim() || !broadcastData.body.trim()) {
      showError('Please fill in title and message');
      return;
    }
    broadcastMutation.mutate(broadcastData);
  };

  const { data, isLoading, error } = useQuery<{ items: Announcement[]; total: number }>({
    queryKey: ['announcements'],
    queryFn: async () => {
      const res = await apiClient.get<{ items: Announcement[]; total: number }>('/settings/announcements', { params: { active_only: false } });
      return res.data;
    },
    enabled: true,
  });

  const createMutation = useMutation({
    mutationFn: (body: AnnouncementFormData) =>
      apiClient.post('/settings/announcements', body).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      showSuccess('Announcement created successfully');
      setOpenDialog(false);
      resetForm();
    },
    onError: (err: any) => {
      showError(err.message || 'Failed to create announcement');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<AnnouncementFormData> & { is_active?: boolean } }) =>
      apiClient.patch(`/settings/announcements/${id}`, body).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      showSuccess('Announcement updated successfully');
      setOpenDialog(false);
      resetForm();
    },
    onError: (err: any) => {
      showError(err.message || 'Failed to update announcement');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/settings/announcements/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      showSuccess('Announcement deleted successfully');
    },
    onError: (err: any) => {
      showError(err.message || 'Failed to delete announcement');
    },
  });

  const resetForm = () => {
    setFormData({ title: '', body: '', is_pinned: false, priority: 'medium' });
    setEditingId(null);
  };

  const handleNew = () => {
    resetForm();
    setOpenDialog(true);
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingId(announcement.id);
    setFormData({
      title: announcement.title,
      body: announcement.body,
      is_pinned: announcement.is_pinned,
      priority: announcement.priority,
    });
    setOpenDialog(true);
  };

  const handleSave = () => {
    if (!formData.title.trim() || !formData.body.trim()) {
      showError('Please fill in all required fields');
      return;
    }

    if (editingId) {
      updateMutation.mutate({ id: editingId, body: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const columns: GridColDef[] = [
    { field: 'title', headerName: 'Title', flex: 1, minWidth: 150 },
    {
      field: 'priority',
      headerName: 'Priority',
      flex: 0.5,
      minWidth: 90,
      renderCell: (params: any) => {
        const colors: Record<string, 'default' | 'error' | 'warning' | 'success'> = {
          low: 'default',
          medium: 'default',
          high: 'warning',
          urgent: 'error',
        };
        return <Chip label={params.value || 'medium'} size="small" color={colors[params.value] || 'default'} />;
      },
    },
    {
      field: 'is_pinned',
      headerName: 'Pinned',
      flex: 0.3,
      minWidth: 60,
      valueGetter: (params: any) => (params.value ? 'Yes' : 'No'),
    },
    {
      field: 'is_active',
      headerName: 'Active',
      flex: 0.3,
      minWidth: 60,
      valueGetter: (params: any) => (params.value ? 'Yes' : 'No'),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      flex: 0.5,
      minWidth: 90,
      valueGetter: (params: any) => formatDate(params.value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 80,
      minWidth: 80,
      getActions: (params: any) => canWrite ? [
        <GridActionsCellItem
          icon={<EditIcon fontSize="small" />}
          label="Edit"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon fontSize="small" />}
          label="Delete"
          onClick={() => {
            setAnnouncementToDelete(params.row.id);
            setDeleteConfirmOpen(true);
          }}
        />,
      ] : [],
    },
  ];

  if (!canWrite) {
    return <Alert severity="warning">{t('You do not have permission to manage announcements')}</Alert>;
  }

  if (error) {
    return <Alert severity="error">{t('Failed to load announcements')}</Alert>;
  }

  return (
    <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="h5" fontWeight={TYPE.semibold}>Announcements</Typography>
              <PageHelpIcon pageKey="settings.announcements" />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CampaignIcon />}
                onClick={() => setBroadcastOpen(true)}
              >
                Notify All Staff
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNew}
              >
                New Announcement
              </Button>
            </Stack>
          </Stack>

          {isLoading ? (
            <Skeleton variant="rectangular" height={400} />
          ) : (
            <DataGrid
              rows={data?.items || []}
              columns={columns}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              autoHeight
              density="compact"
            />
          )}

      {/* Form Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <BrandedDialogTitle>{editingId ? 'Edit Announcement' : 'New Announcement'}</BrandedDialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />

            <RichTextEditor
              label="Body"
              value={formData.body}
              onChange={(html) => setFormData({ ...formData, body: html })}
              placeholder="Write your announcement..."
              minHeight={120}
            />

            <TextField
              label="Priority"
              select
              value={formData.priority || 'medium'}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              SelectProps={{ native: true }}
              fullWidth
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_pinned}
                  onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                />
              }
              label="Pin to top (important announcement)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Broadcast Dialog */}
      <Dialog open={broadcastOpen} onClose={() => setBroadcastOpen(false)} maxWidth="sm" fullWidth>
        <BrandedDialogTitle>Notify All Staff</BrandedDialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send an in-app notification to every active staff member.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Title"
              value={broadcastData.title}
              onChange={(e) => setBroadcastData({ ...broadcastData, title: e.target.value })}
              fullWidth
              required
              placeholder="e.g. Company Town Hall — Friday 3pm"
            />

            <RichTextEditor
              label="Message"
              value={broadcastData.body}
              onChange={(html) => setBroadcastData({ ...broadcastData, body: html })}
              placeholder="Write your message to all staff..."
              minHeight={120}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={broadcastData.send_email}
                  onChange={(e) => setBroadcastData({ ...broadcastData, send_email: e.target.checked })}
                />
              }
              label="Also send via email"
            />

            {isGroupLevel && (
              <FormControlLabel
                control={
                  <Switch
                    checked={!broadcastData.company_only}
                    onChange={(e) => setBroadcastData({ ...broadcastData, company_only: !e.target.checked })}
                  />
                }
                label="Send to all group companies (not just current company)"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBroadcastOpen(false)}>Cancel</Button>
          <Button
            onClick={handleBroadcast}
            variant="contained"
            color="secondary"
            startIcon={<CampaignIcon />}
            disabled={broadcastMutation.isPending}
          >
            {broadcastMutation.isPending ? 'Sending...' : 'Send to All Staff'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Announcement"
        description="Delete this announcement? It will be permanently removed and staff will no longer see it."
        confirmLabel="Delete"
        onConfirm={() => { if (announcementToDelete) deleteMutation.mutate(announcementToDelete); }}
        onClose={() => { setDeleteConfirmOpen(false); setAnnouncementToDelete(null); }}
      />
    </Box>
  );
}
